import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { PaletteOutlined } from "@material-ui/icons";
import { motion, AnimatePresence } from "framer-motion";
import { accents } from "../../assets/theme/accents";

const AccentSwitcher = ({ accent, onChange }) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const handleKeyDown = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    return (
        <div className={classes.root} ref={rootRef}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        className={classes.panel}
                        initial={{ opacity: 0, y: 12, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.97 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <span className={classes.panelTitle}>Accent</span>
                        {accents.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`${classes.option} ${item.id === accent.id ? classes.optionActive : ""}`}
                                onClick={() => {
                                    onChange(item);
                                    setOpen(false);
                                }}
                            >
                                <span
                                    className={classes.dot}
                                    style={{
                                        background: `linear-gradient(135deg, rgb(${item.a1}), rgb(${item.a2}))`,
                                    }}
                                />
                                <span className={classes.optionLabel}>{item.label}</span>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                type="button"
                aria-label="Change accent color"
                className={`${classes.fab} ${open ? classes.fabActive : ""}`}
                onClick={() => setOpen((prev) => !prev)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.94 }}
            >
                <PaletteOutlined className={classes.fabIcon} />
            </motion.button>
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        position: "fixed",
        left: 20,
        bottom: 20,
        zIndex: 1400,
        [theme.breakpoints.down("sm")]: { left: 12, bottom: 12 },
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    },
    fab: {
        width: 46,
        height: 46,
        [theme.breakpoints.down("sm")]: { width: 38, height: 38 },
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: theme.palette.text.secondary,
        background: "rgba(12,12,22,0.72)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
        transition: "color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
            color: "rgb(var(--accent-1-light))",
            borderColor: "rgba(var(--accent-1),0.55)",
            boxShadow: "0 0 24px rgba(var(--accent-1),0.3)",
        },
    },
    fabActive: {
        color: "rgb(var(--accent-1-light))",
        borderColor: "rgba(var(--accent-1),0.55)",
    },
    fabIcon: {
        fontSize: 22,
    },
    panel: {
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "12px 10px",
        marginBottom: 12,
        minWidth: 148,
        borderRadius: 14,
        background: "rgba(12,12,22,0.85)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
    },
    panelTitle: {
        fontFamily: theme.monoFont,
        fontSize: 10,
        letterSpacing: "2.5px",
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        padding: "0 8px 6px",
    },
    option: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "7px 8px",
        border: "1px solid transparent",
        borderRadius: 10,
        background: "transparent",
        cursor: "pointer",
        color: theme.palette.text.primary,
        fontSize: 13,
        fontFamily: theme.typography.fontFamily,
        transition: "background 0.2s ease, border-color 0.2s ease",
        "&:hover": {
            background: "rgba(255,255,255,0.05)",
        },
    },
    optionActive: {
        background: "rgba(var(--accent-1),0.1)",
        borderColor: "rgba(var(--accent-1),0.4)",
    },
    dot: {
        width: 16,
        height: 16,
        borderRadius: "50%",
        flexShrink: 0,
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
    },
    optionLabel: {
        lineHeight: 1,
    },
}));

export default AccentSwitcher;
